import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export type ToastKind = 'info' | 'success' | 'error';
interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  show: (message: string, kind?: ToastKind) => void;
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Notice region. Polite messages go to a `status` live region, errors to an `alert` region.
 * The regions are always mounted (empty) so screen readers register them before content arrives.
 */
export function ToastProvider({ children, timeoutMs = 6000 }: { children: ReactNode; timeoutMs?: number }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const show = useCallback(
    (message: string, kind: ToastKind = 'info') => {
      const id = nextId.current++;
      setItems((xs) => [...xs.slice(-3), { id, kind, message }]);
      // Errors stay until dismissed so they can be read at any pace.
      if (kind !== 'error') window.setTimeout(() => dismiss(id), timeoutMs);
    },
    [dismiss, timeoutMs],
  );
  const api = useMemo<ToastApi>(() => ({ show, success: (m) => show(m, 'success'), error: (m) => show(m, 'error') }), [show]);

  const render = (list: ToastItem[]) =>
    list.map((t) => (
      <div key={t.id} className={`toast toast-${t.kind}`}>
        <span>{t.message}</span>
        <button type="button" className="toast-close" onClick={() => dismiss(t.id)} aria-label="Dismiss notification">
          <span aria-hidden="true">×</span>
        </button>
      </div>
    ));

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="toast-region">
          <div role="status" aria-live="polite" className="stack-sm">
            {render(items.filter((t) => t.kind !== 'error'))}
          </div>
          <div role="alert" aria-live="assertive" className="stack-sm">
            {render(items.filter((t) => t.kind === 'error'))}
          </div>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
